import { GET_ONE_ERROR } from 'constants/errors';

export const ERRORS = {
  UNIQUENESS_CODE: 'code',
  AVAILABLE_STOCK_PRODUCT: 'PSA0001',
  // Backend (ms-inventory) rejects removing a variant-axis value while a live
  // variant still maps to it; error.data.variants lists the offenders.
  AXIS_VALUE_IN_USE: 'PVG0008',
  // Backend rejects adding a new axis to an existing group when existing variants
  // don't have values for that axis; error.data.addedFeatures lists the new axis ids.
  MISSING_VARIANT_MIGRATION: 'PVG0009',
  COMBO_RECIPE_DISASSEMBLY_BLOCKED: 'COMBO_RECIPE_DISASSEMBLY_BLOCKED',
  COMBO_PRODUCT_HAS_STOCK: 'COMBO_PRODUCT_HAS_STOCK',
};

// Combo-composition error references the backend can return from
// POST /products when the create request includes an invalid `combo` block
// (e.g. a component from a different supplier — see CLAUDE.md's atomic-create
// contract). Every reference here has a dedicated, human message under
// product:combo.errors.<reference> (shared with the product-detail combo
// editor's useComboForm) — mapGetOneErrors below surfaces that message
// instead of the generic "bad request" fallback.
export const COMBO_ERROR_REFERENCES = new Set([
  'COMBO_COMPONENT_IS_COMBO',
  'COMBO_COMPONENT_IS_VARIANT_PARENT',
  'COMBO_COMPONENT_DIFFERENT_SUPPLIER',
  'COMBO_COMPONENT_DUPLICATED',
  'COMBO_COMPONENT_IS_SELF',
  'COMBO_COMPONENT_NOT_FOUND',
  'COMBO_EMPTY_COMPOSITION',
  'COMBO_PRODUCT_IS_VARIANT',
]);

export type AxisInUseVariant = { _id: string; code?: string; slug?: string };

/**
 * When updateVariantAxes was blocked because a removed axis value is still used
 * by live variants, returns that list so the UI can tell the user what to delete
 * first. Returns null for any other error.
 */
export const getAxisValueInUseVariants = (error: any): AxisInUseVariant[] | null => {
  if (error?.reference !== ERRORS.AXIS_VALUE_IN_USE) return null;
  const variants = error?.data?.variants;
  return Array.isArray(variants) ? variants : [];
};

/** True when the error is the AXIS_VALUE_IN_USE block (render the dedicated alert
 * instead of the generic HandlerError). */
export const isAxisValueInUseError = (error: any): boolean => error?.reference === ERRORS.AXIS_VALUE_IN_USE;

export const PRODUCT_ERRORS = {
  [ERRORS.UNIQUENESS_CODE]: {
    title: 'product:errors.duplicatedCodeTitle',
    description: 'product:errors.duplicatedCodeDescription',
  },
  [ERRORS.AVAILABLE_STOCK_PRODUCT]: {
    title: 'product:errors.AVAILABLE_STOCK_PRODUCT',
  },
};

export const mapGetOneErrors = (error: any): any => {
  if (error?.status === 404) {
    return GET_ONE_ERROR;
  }
  if (error?.status === 400) {
    if (error?.reference === 'E11000') {
      return error?.key?.map((key: string) => {
        if (PRODUCT_ERRORS[key]) {
          return PRODUCT_ERRORS[key];
        }
        return {
          title: error?.message,
          description: error?.message,
        };
      });
    }

    if (COMBO_ERROR_REFERENCES.has(error?.reference)) {
      return {
        title: 'product:combo.errors.title',
        description: `product:combo.errors.${error?.reference as string}`,
        severity: 'error',
      };
    }

    return {
      title: 'product:errors.badRequest',
      description: 'product:errors.badRequestDescription',
      severity: 'error',
    };
  }
};
